import {
  Document,
  TaskDefinitionType,
  TaskManualStatus,
  TaskState,
  User,
} from "@models";
import {
  DocStatus,
  DocUIStatus,
  DocumentStatus,
  DocumentUIStatus,
  DataKeyWithValue,
} from "@types";
import {
  docStatusDictionary,
  docUIDictionary,
  getDocVersionStatus,
  getLatestDocumentVersion,
  hasDocumentRevision,
  manualTaskStatusDictionary,
} from "./documents";

export const isTaskTypeEditable = (taskType: TaskDefinitionType) =>
  taskType !== TaskDefinitionType.QUALITY_EVENT;

export const mapDocumentVersionsToDataKeys = (
  documents: Document[]
): DataKeyWithValue[] => {
  return documents
    .map((doc) => {
      if (!doc.versions || !doc.versions[0]) return [];

      return doc.versions[0].answers.map((answer) => ({
        id: answer.element,
        value: answer.answer,
      }));
    })
    .flat();
};

export const hasTaskContent = (taskState: TaskState): boolean => {
  const notesSnapshot = taskState.notesDocument?.versions?.[0]?.snapshot?.trim();
  if (notesSnapshot) {
    return true;
  }
  return (taskState.fileDocuments || []).length > 0;
};

export type TaskStatusValue = TaskManualStatus | DocStatus | DocUIStatus;

export const isTaskManualStatus = (
  value: string
): value is TaskManualStatus =>
  value === TaskManualStatus.TODO ||
  value === TaskManualStatus.IN_PROGRESS ||
  value === TaskManualStatus.DONE;

export const normalizeTaskManualStatus = (value: string): TaskManualStatus =>
  isTaskManualStatus(value) ? value : TaskManualStatus.TODO;

export const getDocumentTaskStatus = ({
  documents,
  templateKey,
  user,
  orgId,
}: {
  documents: Document[];
  templateKey: string;
  user: User;
  orgId: string;
}): DocStatus => {
  const matchingDocuments = documents.filter(
    (document) => document.templateKey === templateKey
  );
  if (matchingDocuments.length === 0) {
    return DocumentStatus.AVAILABLE;
  }

  const statusPriority: Record<DocStatus, number> = {
    [DocumentStatus.AVAILABLE]: 0,
    [DocumentStatus.DRAFT]: 1,
    [DocumentStatus.UNAPPROVED]: 2,
    [DocumentStatus.ARCHIVED]: 3,
    [DocumentStatus.LIVE]: 3,
  };

  return matchingDocuments
    .map((document): DocStatus => {
      const latestVersion = getLatestDocumentVersion(document);
      if (!latestVersion) {
        return DocumentStatus.AVAILABLE;
      }

      const latestStatus = getDocVersionStatus({
        selectedDocVersion: latestVersion,
        document,
        user,
        orgId,
      });
      if (latestStatus === DocumentStatus.UNAPPROVED) {
        return DocumentStatus.UNAPPROVED;
      }

      return hasDocumentRevision(document) ? DocumentStatus.LIVE : latestStatus;
    })
    .reduce((lowestStatus, currentStatus) =>
      statusPriority[currentStatus] < statusPriority[lowestStatus]
        ? currentStatus
        : lowestStatus
    );
};

export function getTaskStatusValue(
  params:
    | {
      taskType: "DOCUMENT";
      templateKey: string;
      documents: Document[];
      user: User;
      orgId: string;
      docUIStatus?: DocUIStatus;
    }
    | {
      taskType: "MANUAL" | "APPROVAL" | "QUALITY_EVENT" | "TRAINING";
      manualStatus: TaskManualStatus;
    }
): TaskStatusValue {
  if (params.taskType === "DOCUMENT") {
    if (params.docUIStatus) {
      return params.docUIStatus;
    }
    return getDocumentTaskStatus({
      documents: params.documents,
      templateKey: params.templateKey,
      user: params.user,
      orgId: params.orgId,
    });
  }
  return params.manualStatus;
}

export function getTaskStatusInfo(
  params:
    | { taskType: "DOCUMENT"; status: DocStatus | DocUIStatus }
    | {
      taskType: "MANUAL" | "APPROVAL" | "QUALITY_EVENT" | "TRAINING";
      status: TaskManualStatus;
    }
) {
  if (params.taskType === "DOCUMENT") {
    if (params.status === DocumentUIStatus.LOCKED) {
      return docUIDictionary[DocumentUIStatus.LOCKED];
    }
    return docStatusDictionary[params.status];
  }
  return manualTaskStatusDictionary[params.status];
}
